import type { IssueLayoutOwner } from '../../common/types/issue/issue-layout-owner.tsx';
import type {
	Availability,
	HiddenIn,
	AvailabilityContext,
	HiddenInConfiguration,
	HiddenInConfigurationWithIssueTypes,
} from '../../common/types/layout-items.tsx';
import { NO_CONTEXT } from './constants.tsx';

export const getHiddenInContext = (
	context: AvailabilityContext | null,
	layoutOwners: ReadonlyArray<IssueLayoutOwner>,
	isCustomField: boolean,
) => {
	if (!isCustomField) {
		return null;
	}

	if (context === null) {
		return NO_CONTEXT;
	}

	const { issueTypeIds } = context;

	if (!issueTypeIds.length) {
		return null;
	}

	const currentIssueTypeIds = layoutOwners.map(({ data }) => data.id.toString());
	const isAvailable = issueTypeIds.some((issueTypeId) => currentIssueTypeIds.includes(issueTypeId));

	return isAvailable ? null : context;
};

export const getHiddenInConfigurations = (
	hiddenIn: HiddenIn[] | null,
	layoutOwners: ReadonlyArray<IssueLayoutOwner>,
): HiddenInConfigurationWithIssueTypes[] | null => {
	if (!hiddenIn?.length) {
		return null;
	}

	const layoutOwnersMap: Record<string, string> = {};
	const hiddenInConfigurationsMap: Record<string, HiddenInConfigurationWithIssueTypes> = {};

	for (let i = 0; i < layoutOwners.length; i += 1) {
		layoutOwnersMap[layoutOwners[i].data.id.toString()] = layoutOwners[i].data.name;
	}

	const defaultHiddenIn: HiddenIn | undefined = hiddenIn.find((e) => !e.layoutOwnerId);
	const defaultFieldConfigs: HiddenInConfiguration[] | undefined = defaultHiddenIn
		? defaultHiddenIn.fieldConfigs
		: undefined;
	/**
	 * Actually represents the field config scheme that users have set for all unmapped
	 * issue types.
	 */
	const defaultFieldConfigId: string | undefined =
		!defaultFieldConfigs || defaultFieldConfigs.length === 0
			? undefined
			: defaultFieldConfigs[0].id;

	for (let i = 0; i < hiddenIn.length; i += 1) {
		const currentLayoutOwnerId: string | undefined = hiddenIn[i].layoutOwnerId;
		const currentFieldConfigs: HiddenInConfiguration[] = hiddenIn[i].fieldConfigs;
		/**
		 * If the currentLayoutOwnerId is not undefined (means it is not mapped to the global
		 * "default scheme") and we cannot find it in our layoutOwners, this means that
		 * this field is being hidden in a different screen.
		 */
		if (currentLayoutOwnerId !== undefined && !(currentLayoutOwnerId in layoutOwnersMap)) {
			// eslint-disable-next-line no-continue
			continue;
		}

		/**
		 * If configuration layoutOwnerId is undefined, this issuetype is unmapped and has
		 * been mapped to the "default field configuration scheme" set by the user. They are undefined
		 * because unmapped issue types are not associated with any field config, hence we don't
		 * have their data. To represent these unknown issue types we will set them as 'unknown'
		 * so we can still have a count for our UI.
		 */
		const issueTypeName: string =
			currentLayoutOwnerId === undefined ? 'unknown' : layoutOwnersMap[currentLayoutOwnerId];

		for (let j = 0; j < currentFieldConfigs.length; j += 1) {
			const currentFieldConfig: HiddenInConfiguration = currentFieldConfigs[j];
			const isDefaultFieldConfig: boolean = currentFieldConfig.id === defaultFieldConfigId;

			/**
			 * For the "default" scheme, if issues types are also associated with the same scheme by the user,
			 * this issue type will exist in both hiddenInGlobal and hiddenInIssueType and hence double counted,
			 * to avoid this we only count when issue types are hiddenInGlobal.
			 */
			if (isDefaultFieldConfig && issueTypeName !== 'unknown') {
				// eslint-disable-next-line no-continue
				continue;
			}

			if (hiddenInConfigurationsMap[currentFieldConfig.id]) {
				hiddenInConfigurationsMap[currentFieldConfig.id].fieldConfigIssueTypes.push(issueTypeName);
			} else {
				hiddenInConfigurationsMap[currentFieldConfig.id] = {
					fieldConfigId: currentFieldConfig.id,
					fieldConfigName: currentFieldConfig.name,
					fieldConfigIssueTypes: [issueTypeName],
					isDefaultFieldConfig,
				};
			}
		}
	}

	return Object.values(hiddenInConfigurationsMap);
};

export const getRestrictions = (
	availability: Availability | null,
	layoutOwners: ReadonlyArray<IssueLayoutOwner>,
	isCustomField: boolean,
) => {
	if (availability == null) {
		return null;
	}

	const { hiddenIn, context } = availability;

	const hiddenInContext = getHiddenInContext(context, layoutOwners, isCustomField);
	const hiddenInConfigurationsWithIssueTypes = getHiddenInConfigurations(hiddenIn, layoutOwners);

	if (!hiddenInContext && !hiddenInConfigurationsWithIssueTypes) {
		return null;
	}

	return { hiddenInContext, hiddenInConfigurationsWithIssueTypes };
};
